import React from "react";
import { useTranslation } from "react-i18next";
import { Map as MapIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { SettingsCard, useSaveProfile } from "./primitives";

/**
 * AutoContributeSection — corridor auto-publish toggle. When on, every
 * new identify that includes a location publishes an anonymous pin
 * without needing the per-scan toggle on the Result page. Opt-in only;
 * default off (respect privacy). Backed by `auto_contribute_corridor`
 * in user prefs.
 */
export default function AutoContributeSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { saveProfile } = useSaveProfile();

  const onToggle = (nextValue) => {
    // Optimistically flip via saveProfile — it refetches /auth/me on
    // success (updates AuthContext) or reverts on failure.
    saveProfile(
      { auto_contribute_corridor: nextValue },
      nextValue
        ? t("settings.autoContributeSavedOn")
        : t("settings.autoContributeSavedOff")
    );
  };

  return (
    <SettingsCard
      icon={<MapIcon className="w-4 h-4" strokeWidth={1.5} />}
      title={t("settings.autoContributeTitle")}
      help={t("settings.autoContributeHelp")}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-leaf-ink font-medium">
            {t("settings.autoContributeLabel")}
          </p>
          <p className="mt-1 text-[12px] text-leaf-mist leading-relaxed">
            {user?.auto_contribute_corridor
              ? t("settings.autoContributeOnHint")
              : t("settings.autoContributeOffHint")}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!user?.auto_contribute_corridor}
          aria-label={t("settings.autoContributeToggleAria")}
          onClick={() => onToggle(!user?.auto_contribute_corridor)}
          data-testid="auto-contribute-toggle"
          className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition ${
            user?.auto_contribute_corridor ? "bg-leaf" : "bg-parchment"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              user?.auto_contribute_corridor ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </SettingsCard>
  );
}
